import dedent from 'dedent';
import invariant from 'invariant';
//import * as base64 from 'base64-js';
import lodash from 'lodash';

import defined from '~app/core/defined';
//import moment from '~app/core/moment';
//import DayString from '~app/core/dayString';

import Model from './model';

const Types = {};

function throwError(msg) {
  throw new Error(msg);
}

Types.RAW = function (props) {
  return {
    default: null,
    serialize: obj => {
      if (!defined(obj)) return null;
      if (obj instanceof Object) return obj.valueOf();
      return obj;
    },
    deserialize: obj => (defined(obj) ? obj : null),
    ...props,
  };
};

Types.NUMBER = function (props) {
  return Types.RAW({
    deserialize: obj => (defined(obj) ? Number.parseFloat(obj) : null),
    ...props,
  });
};
Types.INTEGER = function (props) {
  return Types.RAW({
    deserialize: obj => (defined(obj) ? Number.parseInt(obj) : null),
    ...props,
  });
};
Types.INT = Types.INTEGER;

Types.BOOLEAN = function (props) {
  return Types.RAW({
    deserialize: obj => (defined(obj) ? Boolean(obj) : null),
    ...props,
  });
};
Types.BOOL = Types.BOOLEAN;

Types.STRING = function (props) {
  return Types.RAW({
    deserialize: obj => (defined(obj) ? obj.toString() : null),
    ...props,
  });
};
Types.STR = Types.STRING;
Types.TEXT = Types.STRING;

function toNumberOrString(val) {
  if (lodash.isString(val)) {
    try {
      const numVal = Number.parseFloat(val);
      if (!Number.isNaN(numVal)) {
        val = numVal;
      }
    } catch (e) {} // eslint-disable-line no-empty
  }
  return val;
}

Types.NUMBER_OR_STRING = function (props) {
  return Types.RAW({
    deserialize: obj => (defined(obj) ? toNumberOrString(obj) : null),
    ...props,
  });
};

/*
Types.DATE = function (props) {
  return {
    default: null,
    serialize: (obj) => defined(obj) ? DayString(obj).toString() : null,
    deserialize: (obj) => defined(obj) ? DayString(obj).toString() : null,
    ...props,
  };
};

Types.DATETIME = function (props) {
  return {
    default: null,
    serialize: (obj) => defined(obj) ? moment(obj).toISOString() : null,
    deserialize: (obj) => defined(obj) ? moment.tz(obj, 'UTC').toDate() : null,
    ...props,
  };
};

Types.BLOB = function (props) {
  return {
    default: null,
    serialize: (obj) => defined(obj) ? base64.fromByteArray(obj) : null,
    deserialize: (obj) => defined(obj) ? base64.toByteArray(obj) : null,
    ...props,
  };
};
*/

Types.OBJECT = function (props) {
  return {
    default: null,
    object: true,
    serialize: obj => {
      if (!defined(obj)) return null;
      return obj;
    },
    deserialize: obj => (defined(obj) ? obj : null),
    ...props,
  };
};

Types.REF = function (keyPath, props) {
  return {
    ref: true,
    refKeyPath: keyPath,
    readonly: true,
    transient: true,
    serialize: obj => throwError('not implemented'),
    deserialize: obj => throwError('type is readonly'),
    ...props,
  };
};

Types.TRANSIENT = function (props) {
  return {
    default: null,
    transient: true,
    serialize: obj => throwError('type cannot be serialized'),
    deserialize: obj => throwError('type cannot be deserialized'),
    ...props,
  };
};

Types.ID = function (props) {
  return Types.INT({
    primary: true,
    ...props,
  });
};

Types.MODEL = function (modelType, props) {
  let deserialize;
  if (Model.is(modelType)) {
    props = Object.assign({}, props, { modelType });
  } else if (props !== undefined) {
    props = { modelType };
  } else {
    invariant(defined(modelType), 'must define modelType or props');
    props = modelType;
  }

  // custom deserialize always wins
  if (props.deserialize) {
    deserialize = props.deserialize;
  } else if (props.modelType) {
    deserialize = (obj, ctx) =>
      defined(obj) ? Model.deserialize(obj, props.modelType, ctx) : null;
  } else if (props.polymorphicOn) {
    invariant(props.polymorphicMap, 'must specify "polymorphicMap"');
    deserialize = (obj, ctx) => {
      if (!defined(obj)) return null;
      const { modelType, obj: baseObject } = ctx;
      const polymorphicFieldType = modelType.fields[props.polymorphicOn];
      const serializedPolymorphicKey =
        polymorphicFieldType.serializedName || props.polymorphicOn;
      const serializedPolymorphicValue = baseObject[serializedPolymorphicKey];
      const polymorphicValue = polymorphicFieldType.deserialize(
        serializedPolymorphicValue,
        ctx,
      );
      const polymorphicModelType = props.polymorphicMap[polymorphicValue];
      invariant(
        Model.is(polymorphicModelType),
        dedent`
        could not deserialize polymorphic field, "${polymorphicValue}" is \
        not a valid value in the polymorphic mapping \
      `,
      );
      return Model.deserialize(obj, polymorphicModelType, ctx);
    };
  } else {
    invariant(
      props.transient,
      dedent`
      must specify a "modelType", "polymorphicMap", or a custom "deserialize" \
      function for any non-transient model fields \
    `,
    );
    deserialize = obj => throwError('type cannot be deserialized');
  }

  return {
    default: null,
    object: true,
    serialize: (obj, ctx) => Model.serialize(obj, ctx),
    ...props,
    // override the deserialize from props
    deserialize,
  };
};

Types.SELF = function (props) {
  return Types.MODEL({
    ...props,
    deserialize: (obj, ctx) => Model.deserialize(obj, ctx),
  });
};

Types.MAPOF = function ({ ...props } = {}) {
  invariant(
    (defined(props.keyType) && defined(props.valueType)) ||
      (defined(props.serialize) && defined(props.deserialize)),
    'must define keyType and valueType',
  );
  const { keyType, valueType } = props;
  let { serialize, deserialize } = props;
  if (!defined(serialize)) {
    serialize = (obj, ctx) => {
      if (!defined(obj)) return null;
      const result = {};
      for (const [k, v] of Object.entries(obj)) {
        const serializedKey = keyType.serialize(k, ctx);
        invariant(defined(serializedKey), `failed to serialize key=${k}`);
        const serializedValue = valueType.serialize(v, ctx);
        result[serializedKey] = serializedValue;
      }
      return result;
    };
  }
  if (!defined(deserialize)) {
    deserialize = (obj, ctx) => {
      if (!defined(obj)) return null;
      const result = {};
      for (const [serializedKey, serializedValue] of Object.entries(obj)) {
        const k = keyType.deserialize(serializedKey, ctx);
        invariant(
          !defined(serializedKey) || defined(k),
          `failed to deserialize key=${serializedKey}`,
        );
        const v = valueType.deserialize(serializedValue, ctx);
        result[k] = v;
      }
      return result;
    };
  }
  return {
    object: true,
    default: () => ({}),
    ...props,
    serialize,
    deserialize,
  };
};

Types.LISTOF = function (itemType, props) {
  return {
    itemType,
    list: true,
    object: true,
    default: () => [],
    serialize: (obj, ctx) => {
      if (!defined(obj)) return null;
      return Array.from(obj).map(o => itemType.serialize(o, ctx));
    },
    deserialize: (obj, ctx) => {
      if (!defined(obj)) return null;
      return Array.from(obj).map(o => itemType.deserialize(o, ctx));
    },
    ...props,
  };
};

Types.SETOF = function (itemType, props) {
  return {
    itemType,
    list: true,
    object: true,
    default: () => new Set(),
    serialize: (obj, ctx) => {
      if (!defined(obj)) return null;
      return Array.from(obj).map(o => itemType.serialize(o, ctx));
    },
    deserialize: (obj, ctx) => {
      if (!defined(obj)) return null;
      return new Set(Array.from(obj).map(o => itemType.deserialize(o, ctx)));
    },
    ...props,
  };
};

export default Types;
